import type { AccessStatus, MatrixCell } from "../access/api";

/**
 * The status of the installation ([F-025], [AC-045], [NF-019]) as rows the
 * page lays out. Pure, so what the page claims to show is tested.
 *
 * Every Festlegung of [F-025] appears under the key the SRS uses, with its
 * effective value as the running application reports it - the values are the
 * SRS's own codes (`local`, `by_administration`, ...), so the row and the SRS
 * table read the same. Durations are also given in words.
 */

export type StatusValue =
  | { kind: "code"; value: string }
  | { kind: "duration"; seconds: number }
  | { kind: "text"; value: string }
  | { kind: "none" }
  | { kind: "see"; section: "roles" | "relations" | "matrix" };

export interface FestlegungRow {
  /** The key of the SRS table, e.g. `session.idle_limit`. */
  key: string;
  /** The i18n key of the question the row answers. */
  questionKey: string;
  value: StatusValue;
}

const code = (value: string | null | undefined): StatusValue => (value ? { kind: "code", value } : { kind: "none" });

/** The rows in the order of the SRS table [F-025]. */
export function festlegungen(status: AccessStatus): FestlegungRow[] {
  const s = status.session;
  const row = (key: string, value: StatusValue): FestlegungRow => ({
    key,
    questionKey: `adminStatus.questions.${key.replace(".", "_")}`,
    value,
  });
  return [
    row("authentication.method", code(status.authentication.method)),
    row("authentication.account_creation", code(status.authentication.accountCreation)),
    row("session.absolute_limit", { kind: "duration", seconds: s.absoluteSeconds }),
    row("session.end", code(s.end)),
    row("session.absolute_configurable", code(s.absoluteConfigurable ? "bounded" : "fixed")),
    row("session.idle_limit", s.idleSeconds === null ? { kind: "none" } : { kind: "duration", seconds: s.idleSeconds }),
    row("session.warning_lead", { kind: "duration", seconds: s.warningLeadSeconds }),
    row("protection.break_glass", code(status.protection.breakGlass)),
    row("protection.step_up", code(status.protection.stepUp)),
    row(
      "protection.step_up_extra",
      status.protection.stepUpExtra ? { kind: "text", value: status.protection.stepUpExtra } : { kind: "none" },
    ),
    row("authorization.scope", code(status.authorization.scope)),
    row("authorization.roles", { kind: "see", section: "roles" }),
    row("authorization.administration", code(status.authorization.administration)),
    row("authorization.admin_role", code(status.authorization.adminRole)),
    row("authorization.groups", code(status.authorization.groups)),
    row("authorization.account_visibility", code(status.authorization.visibility)),
    row("authorization.relations", { kind: "see", section: "relations" }),
    row("authorization.matrix", { kind: "see", section: "matrix" }),
    row("lifecycle.removal", code(status.lifecycle.removal)),
  ];
}

/** The [NF-019] parameters that are durations in seconds. */
const SECONDS_PARAMETERS = new Set([
  "SESSION_MAX",
  "SESSION_IDLE",
  "SESSION_WARNING_LEAD",
  "ELEVATION_WINDOW",
  "LOGIN_CODE_WINDOW",
  "INVITE_LINK_WINDOW",
]);

export interface ParameterRow {
  name: string;
  value: StatusValue;
  origin: "environment" | "default";
}

/**
 * Every operating parameter with its effective value and where it came from
 * ([NF-019], [AC-069]) - secrets arrive masked from the server and stay so.
 * The order of [NF-019]'s table, then anything else the server reports.
 */
export function parameters(status: AccessStatus): ParameterRow[] {
  const order = [...SECONDS_PARAMETERS, "TIMEZONE", "SYSTEM_USER", "SYSTEM_USER_PASSWORD"];
  const names = Object.keys(status.parameters).sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib) || a.localeCompare(b);
  });
  return names.map((name) => {
    const entry = status.parameters[name] ?? { value: null, origin: "default" as const };
    const raw = entry.value;
    const value: StatusValue =
      raw === null
        ? { kind: "none" }
        : SECONDS_PARAMETERS.has(name) && typeof raw === "number"
          ? { kind: "duration", seconds: raw }
          : { kind: "text", value: String(raw) };
    return { name, value, origin: entry.origin };
  });
}

export type DurationUnit = "hour" | "hours" | "minute" | "minutes" | "second" | "seconds" | "day" | "days";

/** A duration in its largest whole unit: 43200 -> 12 hours, 300 -> 5 minutes. */
export function durationParts(seconds: number): { unit: DurationUnit; n: number } {
  const pick = (n: number, one: DurationUnit, many: DurationUnit) => ({ unit: n === 1 ? one : many, n });
  if (seconds > 0 && seconds % 86400 === 0) return pick(seconds / 86400, "day", "days");
  if (seconds > 0 && seconds % 3600 === 0) return pick(seconds / 3600, "hour", "hours");
  if (seconds > 0 && seconds % 60 === 0) return pick(seconds / 60, "minute", "minutes");
  return pick(seconds, "second", "seconds");
}

/** i18n keys of the units, one place. */
export const DURATION_KEY: Record<DurationUnit, string> = {
  day: "duration.day",
  days: "duration.days",
  hour: "duration.hour",
  hours: "duration.hours",
  minute: "duration.minute",
  minutes: "duration.minutes",
  second: "duration.second",
  seconds: "duration.seconds",
};

/** i18n keys of the matrix cells ([F-027]: yes, no, own - the SRS symbols live in de.ts). */
export const CELL_KEY: Record<MatrixCell, string> = {
  yes: "adminStatus.cell.yes",
  no: "adminStatus.cell.no",
  own: "adminStatus.cell.own",
};

/** i18n keys of the rows that point at a section below. */
export const SEE_KEY: Record<"roles" | "relations" | "matrix", string> = {
  roles: "adminStatus.see.roles",
  relations: "adminStatus.see.relations",
  matrix: "adminStatus.see.matrix",
};
